/**
 * Client-side error reporting → the existing tenant-scoped diagnostics relay
 * (`POST /api/companies/:companyId/diagnostics/relay`, which lands in
 * `session_events` via trackEvent). This is the self-hosted alternative to
 * Sentry: no third-party egress, tenant scoping comes for free from the session
 * cookie the desktop webview already carries.
 *
 * Every relayed event is tagged with the runtime `surface` (desktop / pwa /
 * browser) plus build versions (web sha + Tauri shell version) so a support
 * row instantly says *which client* on *which tenant* hit the error — the main
 * thing that made desktop debugging painful before.
 *
 * All of these funnel through `relayClientEvents` so they share the same tags:
 *  - React `ErrorBoundary` (render-time crashes)
 *  - window `error` / `unhandledrejection`
 *  - TanStack Query cache (`client_query_error` / `client_mutation_error`)
 *  - Eden + raw-fetch HTTP failures (`client_api_error`)
 *  - `notifyError` toasts (`client_toast_error`)
 *
 * The same failure often hits more than one of those. Dedup collapses identical
 * messages on the same page within a few seconds so a 400 is one diagnostics
 * row, not three.
 */

import { extractErrorCode, extractErrorMessage } from "./api/errors"
import { apiUrl, IS_STANDALONE_PWA, IS_TAURI } from "./runtime-origin"

export type TClientSurface = "desktop" | "pwa" | "browser"

type TSeverity = "debug" | "info" | "warn" | "error"

interface IClientEvent {
  eventType: string
  severity?: TSeverity
  message: string
  meetingId?: string
  context?: Record<string, unknown>
  occurredAt?: string
}

const WEB_SHA = (import.meta.env.VITE_COMMIT_SHA ?? "").slice(0, 7)
// Hard cap per page load so a render/throw loop can never hammer the relay.
const MAX_EVENTS_PER_SESSION = 50

// ---------------------------------------------------------------------------
// PII scrubbing — this relay persists into `session_events` (30-day retention).
// React crashes routinely embed the data that caused them (meeting subjects,
// transcript fragments, speaker emails), and arbitrary backend error envelopes
// get JSON-stringified into `message`. Scrub before egress so durable telemetry
// on a GDPR-headline product doesn't incidentally capture user content.
// ---------------------------------------------------------------------------
const MAX_TEXT_LEN = 4000
const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi
const URL_KEYS = new Set(["url", "source", "href"])

function clampText(value: string): string {
  const redacted = value.replace(EMAIL_RE, "[redacted-email]")
  return redacted.length > MAX_TEXT_LEN ? `${redacted.slice(0, MAX_TEXT_LEN)}… [truncated]` : redacted
}

/** Drop the query + hash (may carry tokens/PII) and mask id-ish path segments. */
function scrubUrl(raw: string): string {
  try {
    const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost"
    const u = new URL(raw, origin)
    const path = u.pathname
      .split("/")
      .map((seg) => (seg && (seg.length >= 12 || /^\d+$/.test(seg) || seg.includes("_")) ? ":id" : seg))
      .join("/")
    return `${u.origin}${path}`
  } catch {
    return "[unparseable-url]"
  }
}

function scrubContext(ctx: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!ctx) return ctx
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(ctx)) {
    out[k] = typeof v === "string" ? (URL_KEYS.has(k) ? scrubUrl(v) : clampText(v)) : v
  }
  return out
}

export interface IReportClientErrorExtra {
  eventType?: string
  title?: string
  context?: Record<string, unknown>
}

let activeCompanyId: string | null = null
let shellVersion: string | undefined
let relayedCount = 0
let installed = false

const DEDUP_WINDOW_MS = 5_000
const recentFingerprints = new Map<string, number>()

/** Which shell the SPA is running in — tags every relayed event for fast filtering. */
function clientSurface(): TClientSurface {
  if (IS_TAURI) return "desktop"
  if (IS_STANDALONE_PWA) return "pwa"
  return "browser"
}

/** `__root` keeps the active org in sync so out-of-React handlers can scope the relay. */
export function setReportingCompany(companyId: string | null): void {
  activeCompanyId = companyId
}

/** `__root` sets this once the Tauri shell version resolves (desktop only). */
export function setReportingShellVersion(version: string): void {
  shellVersion = version
}

function fingerprintFor(message: string): string {
  const path = typeof window !== "undefined" ? window.location.pathname : ""
  return `${path}|${message}`
}

function isDuplicateMessage(message: string): boolean {
  const key = fingerprintFor(message)
  const now = Date.now()
  const last = recentFingerprints.get(key)
  if (last !== undefined && now - last < DEDUP_WINDOW_MS) return true
  recentFingerprints.set(key, now)
  if (recentFingerprints.size > 80) {
    for (const [k, t] of recentFingerprints) {
      if (now - t >= DEDUP_WINDOW_MS) recentFingerprints.delete(k)
    }
  }
  return false
}

/** Session expiry, aborted fetches, and query cancellation are expected noise. */
export function isExpectedClientNoise(err: unknown): boolean {
  if (err instanceof Error) {
    if (err.name === "AbortError" || err.name === "CancelledError" || err.name === "CanceledError") {
      return true
    }
  }
  if (extractErrorCode(err) === "UNAUTHORIZED") return true
  const message = extractErrorMessage(err)
  return message === "HTTP 401" || message === "Authentication required" || message === "Authentication failed"
}

/**
 * Relay a batch of client events, enriched with the runtime surface + build
 * versions. Returns false (and relays nothing) when no tenant is active, so
 * callers can fall back to console.
 */
export function relayClientEvents(events: IClientEvent[]): boolean {
  const companyId = activeCompanyId
  if (!companyId || events.length === 0) return false
  if (relayedCount >= MAX_EVENTS_PER_SESSION) return false

  const unique = events.filter((ev) => !isDuplicateMessage(clampText(ev.message)))
  if (unique.length === 0) return false
  relayedCount += unique.length

  const enriched = unique.map((ev) => ({
    eventType: ev.eventType,
    severity: ev.severity ?? "error",
    // `message` and free-form context (stack, componentStack, url, …) are scrubbed
    // before they leave the client; `meetingId` is an internal correlation id (not
    // PII) and the surface/version tags are appended unscrubbed.
    message: clampText(ev.message),
    meetingId: ev.meetingId,
    occurredAt: ev.occurredAt ?? new Date().toISOString(),
    context: {
      ...scrubContext(ev.context),
      surface: clientSurface(),
      webSha: WEB_SHA,
      ...(shellVersion ? { shellVersion } : {}),
    },
  }))

  fetch(apiUrl(`/api/companies/${companyId}/diagnostics/relay`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ events: enriched }),
  }).catch(() => {
    // Fire-and-forget — telemetry must never mask the original error.
  })
  return true
}

function meetingIdFromLocation(): string | undefined {
  if (typeof window === "undefined") return undefined
  const match = window.location.pathname.match(/\/meetings\/([^/]+)/)
  return match?.[1]
}

/**
 * Relay a handled error (toast, query/mutation failure, failed Snap, HTTP 4xx).
 * Uncaught crashes already go through the window handlers; this is the path
 * for errors the UI caught — a 400 that only showed a toast, or a query that
 * only flipped an error state, never used to reach diagnostics.
 */
export function reportClientError(err: unknown, extra?: IReportClientErrorExtra): boolean {
  if (isExpectedClientNoise(err)) return false
  return relayClientEvents([
    {
      eventType: extra?.eventType ?? "client_handled_error",
      severity: "error",
      message: extractErrorMessage(err),
      meetingId: meetingIdFromLocation(),
      context: {
        title: extra?.title,
        name: err instanceof Error ? err.name : undefined,
        url: typeof window !== "undefined" ? window.location.href : undefined,
        ...extra?.context,
      },
    },
  ])
}

/**
 * Relay a failed HTTP response from Eden or a raw `fetch`. 401s are skipped
 * (session expiry is expected). Callers that already parsed the body should
 * pass it so the real API message is stored, not just `HTTP 400`.
 */
export function reportFailedResponse(response: Response, body?: unknown): boolean {
  if (response.ok || response.status === 401) return false
  if (response.url.includes("/diagnostics/relay")) return false
  return reportClientError(body ?? `HTTP ${response.status}`, {
    eventType: "client_api_error",
    title: `HTTP ${response.status}`,
    context: { status: response.status, url: response.url },
  })
}

/** Test-only: clear the per-session cap and dedup window. */
export function resetClientErrorReportingForTests(): void {
  relayedCount = 0
  recentFingerprints.clear()
}

/**
 * Install global window error + promise-rejection handlers once. Uncaught JS
 * errors and unhandled rejections go to the relay (when a tenant is active).
 * Resource-load failures (img/script 404s) are ignored to avoid noise.
 */

export function installGlobalErrorReporting(): void {
  if (installed || typeof window === "undefined") return
  installed = true

  window.addEventListener("error", (event: ErrorEvent) => {
    const err = event.error
    // Resource-load failures surface here with no Error object — skip them.
    if (!(err instanceof Error)) return
    relayClientEvents([
      {
        eventType: "client_uncaught_error",
        severity: "error",
        message: err.message,
        context: {
          name: err.name,
          stack: err.stack,
          source: event.filename,
          line: event.lineno,
          column: event.colno,
          url: window.location.href,
        },
      },
    ])
  })

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reason: unknown = event.reason
    const isError = reason instanceof Error
    relayClientEvents([
      {
        eventType: "client_unhandled_rejection",
        severity: "error",
        message: isError ? reason.message : String(reason),
        context: {
          name: isError ? reason.name : undefined,
          stack: isError ? reason.stack : undefined,
          url: window.location.href,
        },
      },
    ])
  })
}
